import { BrowserModule } from '@angular/platform-browser';
import { NgModule, ErrorHandler, CUSTOM_ELEMENTS_SCHEMA, APP_INITIALIZER } from '@angular/core';
import { provideHttpClient, withInterceptorsFromDi, HTTP_INTERCEPTORS } from '@angular/common/http';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { registerLocaleData } from '@angular/common';
import localeGb from '@angular/common/locales/en-GB';

import { NgbModule } from '@ng-bootstrap/ng-bootstrap';

import { AppComponent } from './app.component';
import { AppRoutingModule } from './app-routing.module';
import { SharedModule } from './shared/shared.module';
import { AuthModule } from './auth/auth.module'
import { CoreModule } from './core/core.module';

import { Interview2QuestionModule } from './interview2-question/interview2-question.module';
import { Interview3QuestionModule } from './interview3-question/interview3-question.module';
import { QuestionModule } from './question/question.module';


import { ResultModule } from './result/result.module';
import { WelcomeModule } from './welcome/welcome.module';
import { IntroComponent } from './intro/intro.component';

import { RatingModule } from './rating/rating.module';
import { StartComponent } from './start/start.component';
import { QuizComponent } from './quiz/quiz.component';
import { ClientComponent } from './client/client.component';

import { MatPaginatorModule } from "@angular/material/paginator";
import { MatSortModule } from "@angular/material/sort";
import { MatTableModule } from "@angular/material/table";
import { MatCheckboxModule } from "@angular/material/checkbox";
import { MatIconModule } from "@angular/material/icon";
import { MatAutocompleteModule } from "@angular/material/autocomplete";
import { MatInputModule } from "@angular/material/input";
import { MatFormFieldModule } from "@angular/material/form-field";
import { MatSelectModule } from "@angular/material/select";
import { MatDatepickerModule } from "@angular/material/datepicker";
import { MatNativeDateModule, MAT_DATE_LOCALE } from "@angular/material/core";

import { ReactiveFormsModule, FormsModule } from '@angular/forms';
import { ClientDetailComponent } from './client-detail/client-detail.component';
import { CodeComponent } from './code/code.component';
import { CodeDetailComponent } from './code-detail/code-detail.component';
import { TemplateComponent } from './template/template.component';
import { ContentComponent } from './content/content.component';
import { ContentDetailComponent } from './content-detail/content-detail.component';
import { ManageVideosComponent } from './manage-videos/manage-videos.component';
import { VideoDetailComponent } from './video-detail/video-detail.component';
import { UserComponent } from './user/user.component';
import { ReportComponent } from './report/report.component';
import { LibraryComponent } from './library/library.component';
import { EnderComponent } from './ender/ender.component';
import { PodcastComponent } from './podcast/podcast.component';
import { FullnameComponent } from './fullname/fullname.component';
import { ActionReportComponent } from './action-report/action-report.component';
import { ResearchComponent } from './research/research.component';
import { ReflectionsComponent } from './reflections/reflections.component';
import { ClLinkComponent } from './cl-link/cl-link.component';
import { PreviewTrainingComponent } from './preview-training/preview-training.component';
import { MonitorComponent } from './monitor/monitor.component';
import { SendReflectionsComponent } from './send-reflections/send-reflections.component';
import { YesNoComponent } from './yes-no/yes-no.component';
import { SharedReflectionsComponent } from './shared-reflections/shared-reflections.component';
import { ShareUserGuidesComponent } from './share-user-guides/share-user-guides.component';
import { BulkClientComponent } from './bulk-client/bulk-client.component';
import { BulkTagDialogComponent } from './bulk-tag-dialog/bulk-tag-dialog.component';
import { BulkContractDialogComponent } from './bulk-contract-dialog/bulk-contract-dialog.component';
import { BulkCodeDialogComponent } from './bulk-code-dialog/bulk-code-dialog.component';
import { FeedbackComponent } from './feedback/feedback.component';
import { LikertComponent } from './likert/likert.component';
import { DropdownComponent } from './dropdown/dropdown.component';
import { FeedbackQuestionComponent } from './feedback-question/feedback-question.component';
import { TemplateKeyModalComponent } from './feedback-question/template-key-modal/template-key-modal.component';
import { UserDetailComponent } from './user-detail/user-detail.component';
import { ReferencesComponent } from './references/references.component';
import { CsmReportComponent } from './csm-report/csm-report.component';
import { DialogConfirmComponent } from './dialog-confirm/dialog-confirm.component';
import { DialogInputComponent } from './dialog-input/dialog-input.component';
import { SsoComponent } from './sso/sso.component';
import { ContentEditorComponent } from './content-editor/content-editor.component';
import { AdhocReportComponent } from './adhoc-report/adhoc-report.component';
import { NewReportModalComponent } from './adhoc-report/new-report-modal/new-report-modal.component';
import { TagManagementComponent } from './tag-management/tag-management.component';

export class RavenErrorHandler implements ErrorHandler {
  handleError(err: any): void {
    console.log(err)
  }
}

import { GlobalSearchReplaceComponent } from './content/global-search-replace/global-search-replace.component';
import { AuthInterceptor } from './shared/interceptors/auth.interceptor';
import { AuthService } from './auth/auth.service';

export function initializeApp(authService: AuthService) {
  return () => authService.bootstrapToken();
}

@NgModule({
  declarations: [
    AppComponent,
    IntroComponent,
    StartComponent,
    QuizComponent,
    ClientComponent,
    ClientDetailComponent,
    CodeComponent,
    CodeDetailComponent,
    TemplateComponent,
    ContentComponent,
    ContentDetailComponent,
    ManageVideosComponent,
    VideoDetailComponent,
    GlobalSearchReplaceComponent,
    UserComponent,
    ReportComponent,
    LibraryComponent,
    EnderComponent,
    PodcastComponent,
    FullnameComponent,
    ActionReportComponent,
    ResearchComponent,
    ReflectionsComponent,
    ClLinkComponent,
    PreviewTrainingComponent,
    MonitorComponent,
    SendReflectionsComponent,
    YesNoComponent,
    SharedReflectionsComponent,
    ShareUserGuidesComponent,
    BulkClientComponent,
    BulkTagDialogComponent,
    BulkContractDialogComponent,
    BulkCodeDialogComponent,
    FeedbackComponent,
    LikertComponent,
    DropdownComponent,
    FeedbackQuestionComponent,
    TemplateKeyModalComponent,
    UserDetailComponent,
    ReferencesComponent,
    CsmReportComponent,
    DialogConfirmComponent,
    DialogInputComponent,
    SsoComponent,
    AdhocReportComponent,
    NewReportModalComponent,
    TagManagementComponent,
  ],
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
  bootstrap: [AppComponent], imports: [NgbModule,
    BrowserModule,
    AppRoutingModule,
    SharedModule,
    AuthModule,
    CoreModule,
    Interview2QuestionModule,
    Interview3QuestionModule,
    MatCheckboxModule,
    QuestionModule,
    ResultModule,
    WelcomeModule,
    BrowserAnimationsModule,
    RatingModule,
    MatTableModule,
    ReactiveFormsModule,
    FormsModule,
    MatSortModule,
    MatSortModule,
    MatPaginatorModule,
    MatIconModule,
    MatAutocompleteModule,
    MatAutocompleteModule,
    ContentEditorComponent,
    MatInputModule,
    MatFormFieldModule,
    MatSelectModule,
    MatDatepickerModule,
    MatNativeDateModule], providers: [{ provide: ErrorHandler, useClass: RavenErrorHandler }, { provide: MAT_DATE_LOCALE, useValue: 'en-GB' }, { provide: HTTP_INTERCEPTORS, useClass: AuthInterceptor, multi: true }, { provide: APP_INITIALIZER, useFactory: initializeApp, deps: [AuthService], multi: true }, provideHttpClient(withInterceptorsFromDi())]
})
export class AppModule {
  constructor() {
    registerLocaleData(localeGb);
  }
}
